import { z } from 'zod';
import { CARD_OPERATION_STATUSES, CARD_STATUSES, CARD_TRANSACTION_STATUSES } from './card.types';

const RawAmount = z
  .string()
  .regex(/^-?\d+$/)
  .transform(BigInt);

const RawAmountNullable = RawAmount.nullable().catch(null);

const IsoDate = z.string();

const CardStatusSchema = z.enum(CARD_STATUSES).catch('UNKNOWN' as never);

const OperationStatusSchema = z.enum(CARD_OPERATION_STATUSES).catch('UNKNOWN' as never);

const OperationTypeSchema = z.enum(['ISSUE', 'TOP_UP', 'PAYOUT']).catch('UNKNOWN' as never);

const TransactionStatusSchema = z.enum(CARD_TRANSACTION_STATUSES).catch('UNKNOWN' as never);

const DirectionSchema = z.enum(['incoming', 'outgoing']).catch('unknown' as never);

export const CardSchema = z.object({
  id: z.string(),
  cardName: z.string(),
  last4Digits: z.string(),
  currencyCode: z.string(),
  balanceMinor: RawAmount,
  balance: z.string(),
  currencyPrecision: z.number().int(),
  balanceSyncedAt: IsoDate.nullable(),
  paymentSystem: z.string().nullable(),
  status: CardStatusSchema,
  features: z.array(z.string()),
  tokenizationSystems: z.array(z.string()),
  walletInstruction: z.string().nullable(),
  expiresAt: z.string().nullable(),
  issuedAt: IsoDate.nullable(),
  createdAt: IsoDate,
  /** CARD-002: masked provider e-mail; null for cards issued before the change. */
  providerEmail: z.string().nullable().catch(null),
  /** §17: ready-made top-up minimum; tolerant — null is meaningful and a pre-§17 backend omits it. */
  minTopUpSourceAmountRaw: RawAmountNullable,
  minTopUpFiatAmountMinor: RawAmountNullable,
});

export const CardOperationSchema = z.object({
  id: z.string(),
  type: OperationTypeSchema,
  status: OperationStatusSchema,
  cardId: z.string().nullable(),
  sourceAmountRaw: RawAmount,
  netAmountRaw: RawAmount,
  serviceFeeRaw: RawAmount,
  sourceAmount: z.string(),
  fiatAmountMinor: RawAmountNullable,
  fiatCurrency: z.string().nullable(),
  fiatCurrencyPrecision: z.number().int().nullable(),
  fundingIntentId: z.string().nullable(),
  failureReason: z.string().nullable(),
  createdAt: IsoDate,
  updatedAt: IsoDate,
});

export const CardTypeSchema = z
  .object({
    vendorId: z.string(),
    // CARD-002 fields are tolerant: a backend that predates them must not break the issue screen.
    displayName: z.string().catch(''),
    description: z.string().nullable().catch(null),
    maxPerUser: z.number().int().nullable().catch(null),
    remainingForUser: z.number().int().nullable().catch(null),
    forbiddenEmailDomains: z.array(z.string()).catch([]),
    maximumCardsPerEmail: z.number().int().nullable().catch(null),
    currencyCode: z.string(),
    paymentSystem: z.string(),
    issuancePriceRaw: RawAmount,
    initialBalanceRaw: RawAmount,
    serviceFeeRaw: RawAmount,
    totalAmountRaw: RawAmount,
    totalAmount: z.string(),
    issuanceOpen: z.boolean(),
    estimatedIssuanceAt: IsoDate.nullable(),
    tokenizationSystems: z.array(z.string()),
    terms: z.record(z.string(), z.unknown()).catch({}),
  })
  .passthrough();

export const CardTopupQuoteSchema = z.object({
  cardId: z.string(),
  sourceAmountRaw: RawAmount,
  sourceAmount: z.string(),
  netAmountRaw: RawAmount,
  serviceFeeRaw: RawAmount,
  currencyCode: z.string(),
  currencyPrecision: z.number().int(),
  estimatedFiatAmountMinor: RawAmountNullable,
  estimatedFiatAmount: z.string().nullable(),
  estimatedRate: z.number().nullable(),
  isFiatEstimate: z.boolean(),
  minNetAmountRaw: RawAmount,
  maxNetAmountRaw: RawAmount,
  minSourceAmountRaw: RawAmountNullable,
  minFiatAmountMinor: RawAmountNullable,
  expiresAt: IsoDate,
  estimatedCompletionAt: IsoDate,
});

export const FundingFeeQuoteSchema = z.object({
  feeMode: z.enum(['GASLESS_TOKEN', 'NETWORK_NATIVE']),
  feeTokenAddress: z.string(),
  feeTokenDecimals: z.number().int().nonnegative(),
  networkFeeRaw: RawAmount,
  feeUsd: z.string().nullable().catch(null),
  sendAmountRaw: RawAmountNullable,
  debitAmountRaw: RawAmountNullable,
  maxSendableRaw: RawAmountNullable,
  validUntil: IsoDate,
});

export const CardTransactionSchema = z.object({
  id: z.string(),
  cardId: z.string(),
  amountMinor: RawAmount,
  amount: z.string(),
  currencyCode: z.string(),
  currencyPrecision: z.number().int(),
  status: TransactionStatusSchema,
  type: z.string(),
  direction: DirectionSchema,
  description: z.string().nullable(),
  merchantName: z.string().nullable(),
  processingFeeMinor: RawAmountNullable,
  billingAmountMinor: RawAmountNullable,
  billingCurrency: z.string().nullable(),
  occurredAt: IsoDate,
});

export const CardStatisticsSchema = z.object({
  from: IsoDate,
  to: IsoDate,
  totals: z.array(
    z.object({
      currencyCode: z.string(),
      currencyPrecision: z.number().int(),
      spentMinor: RawAmount,
      receivedMinor: RawAmount,
      processingFeeMinor: RawAmount,
      transactionCount: z.number().int(),
      declinedCount: z.number().int(),
    }),
  ),
  monthly: z.array(
    z.object({
      month: z.string(),
      currencyCode: z.string(),
      currencyPrecision: z.number().int(),
      spentMinor: RawAmount,
      receivedMinor: RawAmount,
    }),
  ),
  topMerchants: z.array(
    z.object({
      merchantName: z.string(),
      currencyCode: z.string(),
      currencyPrecision: z.number().int(),
      spentMinor: RawAmount,
      transactionCount: z.number().int(),
    }),
  ),
  balances: z.array(
    z.object({
      cardId: z.string(),
      currencyCode: z.string(),
      currencyPrecision: z.number().int(),
      balanceMinor: RawAmount,
      balance: z.string(),
      balanceSyncedAt: IsoDate.nullable(),
    }),
  ),
});

export const CardSensitiveSchema = z.object({
  embedUrl: z.string().nullable(),
  cardNumber: z.string().nullable(),
  expiryDate: z.string().nullable(),
  cvv: z.string().nullable(),
  /** §16.1: ATM-card PIN. Independent of the embedUrl/details split — may accompany either form. */
  pinCode: z.string().nullable().catch(null),
});

export const ThreeDsCodeSchema = z.object({
  id: z.string(),
  type: z.string(),
  amount: z.number(),
  currency: z.string(),
  code: z.string().nullable(),
  merchantName: z.string().nullable(),
  expiresAt: IsoDate,
});

export const WalletApprovalSchema = z.object({ requested: z.boolean() });

export const ApproveThreeDsSchema = z.object({ approved: z.boolean() });

export function paginated<T extends z.ZodTypeAny>(item: T) {
  return z.object({ data: z.array(item), count: z.number().int() });
}

export const CardListSchema = paginated(CardSchema);
export const CardOperationListSchema = paginated(CardOperationSchema);
export const CardTransactionListSchema = paginated(CardTransactionSchema);
export const CardTypeListSchema = z.array(CardTypeSchema);
export const ThreeDsCodeListSchema = z.array(ThreeDsCodeSchema);
